import { useMemo, useState, type FormEvent } from "react";
import type { AppLanguage } from "../app/language";
import type { Expense, ExpenseVendor, ExpenseVendorCreateInput } from "../app/types";
import { AnimatedModal } from "../app/ui";

type VendorsViewProps = {
  vendors: ExpenseVendor[];
  expenses: Expense[];
  currency: string;
  language: AppLanguage;
  isLoading?: boolean;
  onCreateVendor: (input: ExpenseVendorCreateInput) => void;
  onUpdateVendor: (vendorId: string, input: ExpenseVendorCreateInput) => void;
  onDeleteVendor: (vendorId: string) => void;
};

type VendorSummary = {
  vendor: ExpenseVendor;
  expenseCount: number;
  totalSpend: number;
  lastExpenseDate: string;
};

function formatAmount(amount: number, currency: string) {
  const normalizedCurrency = (currency || "USD").trim().toUpperCase();

  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: normalizedCurrency, maximumFractionDigits: 2 }).format(amount);
  } catch {
    return `${normalizedCurrency} ${amount.toFixed(2)}`;
  }
}

function formatDateLabel(expenseDate: string, language: AppLanguage) {
  if (!expenseDate) {
    return "—";
  }

  const timestamp = Date.parse(`${expenseDate.slice(0, 10)}T00:00:00Z`);

  if (!Number.isFinite(timestamp)) {
    return expenseDate;
  }

  return new Intl.DateTimeFormat(language === "ja" ? "ja-JP" : "en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(timestamp);
}

function CountSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function StatCard({ label, value, detail, isLoading = false }: { label: string; value: string; detail: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{label}</p>
      <div className="mt-4 min-h-[2.25rem]">
        {isLoading ? <CountSpinner /> : <p className="text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

const modalInputClassName =
  "mt-2 w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]";
const modalLabelClassName = "text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]";

const emptyVendorDraft: ExpenseVendorCreateInput = {
  name: "",
  category: "",
};

type VendorsViewCopy = {
  financeEyebrow: string;
  pageTitle: string;
  pageDescription: string;
  newVendor: string;
  statVendors: string;
  statVendorsDetail: string;
  statCategories: string;
  statCategoriesDetail: string;
  statTotalSpend: string;
  statTotalSpendDetail: string;
  allVendors: string;
  shownOf: (shown: number, total: number) => string;
  searchLabel: string;
  searchPlaceholder: string;
  emptyTitle: string;
  emptyDetail: string;
  noMatches: (query: string) => string;
  colVendor: string;
  colCategory: string;
  colExpenses: string;
  colTotalSpend: string;
  colLastExpense: string;
  edit: string;
  delete: string;
  createEyebrow: string;
  createTitle: string;
  createDescription: string;
  nameLabel: string;
  namePlaceholder: string;
  categoryLabel: string;
  categoryPlaceholder: string;
  cancel: string;
  addVendor: string;
  editEyebrow: string;
  editTitle: string;
  editDescription: string;
  saveChanges: string;
};

const vendorsViewCopy: Record<AppLanguage, VendorsViewCopy> = {
  en: {
    financeEyebrow: "Finance",
    pageTitle: "Vendors",
    pageDescription: "Create and manage the vendors you record expenses against.",
    newVendor: "New vendor",
    statVendors: "Vendors",
    statVendorsDetail: "Active vendor records",
    statCategories: "Categories",
    statCategoriesDetail: "Distinct vendor categories",
    statTotalSpend: "Total spend",
    statTotalSpendDetail: "Across all vendor expenses",
    allVendors: "All vendors",
    shownOf: (shown, total) => `${shown} of ${total} shown`,
    searchLabel: "Search vendors",
    searchPlaceholder: "Search vendors…",
    emptyTitle: "No vendors yet.",
    emptyDetail: "Use “New vendor” to add your first vendor.",
    noMatches: query => `No vendors match “${query}”.`,
    colVendor: "Vendor",
    colCategory: "Category",
    colExpenses: "Expenses",
    colTotalSpend: "Total spend",
    colLastExpense: "Last expense",
    edit: "Edit",
    delete: "Delete",
    createEyebrow: "New vendor",
    createTitle: "Add a vendor",
    createDescription: "Vendors group the expenses you record.",
    nameLabel: "Name",
    namePlaceholder: "Vendor name",
    categoryLabel: "Category",
    categoryPlaceholder: "e.g. Software, Travel",
    cancel: "Cancel",
    addVendor: "Add vendor",
    editEyebrow: "Edit vendor",
    editTitle: "Update vendor",
    editDescription: "Changes apply to all of this vendor’s expenses.",
    saveChanges: "Save changes",
  },
  ja: {
    financeEyebrow: "財務",
    pageTitle: "ベンダー",
    pageDescription: "経費を記録する対象となるベンダーを作成・管理します。",
    newVendor: "ベンダーを追加",
    statVendors: "ベンダー",
    statVendorsDetail: "有効なベンダー件数",
    statCategories: "カテゴリ",
    statCategoriesDetail: "ベンダーカテゴリの種類数",
    statTotalSpend: "合計支出",
    statTotalSpendDetail: "全ベンダーの経費合計",
    allVendors: "すべてのベンダー",
    shownOf: (shown, total) => `${total} 件中 ${shown} 件表示`,
    searchLabel: "ベンダーを検索",
    searchPlaceholder: "ベンダーを検索…",
    emptyTitle: "ベンダーがまだありません。",
    emptyDetail: "「ベンダーを追加」から最初のベンダーを登録してください。",
    noMatches: query => `「${query}」に一致するベンダーはありません。`,
    colVendor: "ベンダー",
    colCategory: "カテゴリ",
    colExpenses: "経費件数",
    colTotalSpend: "合計支出",
    colLastExpense: "直近の経費",
    edit: "編集",
    delete: "削除",
    createEyebrow: "ベンダーを追加",
    createTitle: "ベンダーを追加",
    createDescription: "ベンダーは記録する経費をまとめます。",
    nameLabel: "名前",
    namePlaceholder: "ベンダー名",
    categoryLabel: "カテゴリ",
    categoryPlaceholder: "例: ソフトウェア、出張",
    cancel: "キャンセル",
    addVendor: "ベンダーを追加",
    editEyebrow: "ベンダーを編集",
    editTitle: "ベンダーを更新",
    editDescription: "変更はこのベンダーのすべての経費に適用されます。",
    saveChanges: "変更を保存",
  },
};

export function VendorsView({
  vendors,
  expenses,
  currency,
  language,
  isLoading = false,
  onCreateVendor,
  onUpdateVendor,
  onDeleteVendor,
}: VendorsViewProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState<ExpenseVendorCreateInput>(emptyVendorDraft);
  const [editingVendorId, setEditingVendorId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ExpenseVendorCreateInput>(emptyVendorDraft);
  const copy = vendorsViewCopy[language];

  const summaries = useMemo<VendorSummary[]>(() => {
    const totals = new Map<string, { count: number; total: number; lastDate: string }>();

    for (const expense of expenses) {
      const entry = totals.get(expense.vendorId) ?? { count: 0, total: 0, lastDate: "" };
      entry.count += 1;
      entry.total += expense.amount;
      if (expense.expenseDate > entry.lastDate) {
        entry.lastDate = expense.expenseDate;
      }
      totals.set(expense.vendorId, entry);
    }

    return vendors
      .map(vendor => {
        const entry = totals.get(vendor.id);
        return {
          vendor,
          expenseCount: entry?.count ?? 0,
          totalSpend: entry?.total ?? 0,
          lastExpenseDate: entry?.lastDate ?? "",
        };
      })
      .sort((left, right) => right.totalSpend - left.totalSpend || left.vendor.name.localeCompare(right.vendor.name));
  }, [vendors, expenses]);

  const filteredSummaries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return summaries;
    }

    return summaries.filter(({ vendor }) => vendor.name.toLowerCase().includes(query) || vendor.category.toLowerCase().includes(query));
  }, [summaries, searchQuery]);

  const totalSpend = useMemo(() => expenses.reduce((sum, expense) => sum + expense.amount, 0), [expenses]);
  const categoryCount = useMemo(() => new Set(vendors.map(vendor => vendor.category.trim()).filter(Boolean)).size, [vendors]);

  const closeCreateModal = () => {
    setIsCreateOpen(false);
    setCreateDraft(emptyVendorDraft);
  };

  const closeEditModal = () => {
    setEditingVendorId(null);
    setEditDraft(emptyVendorDraft);
  };

  const handleStartEdit = (vendor: ExpenseVendor) => {
    setEditingVendorId(vendor.id);
    setEditDraft({ name: vendor.name, category: vendor.category });
  };

  const handleCreate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = createDraft.name.trim();

    if (!name) {
      return;
    }

    onCreateVendor({ name, category: createDraft.category.trim() });
    closeCreateModal();
  };

  const handleEdit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = editDraft.name.trim();

    if (!editingVendorId || !name) {
      return;
    }

    onUpdateVendor(editingVendorId, { name, category: editDraft.category.trim() });
    closeEditModal();
  };

  return (
    <>
      <section className="space-y-8 p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.financeEyebrow}</p>
            <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.pageTitle}</h1>
            <p className="mt-3 max-w-3xl text-sm text-[#667085]">{copy.pageDescription}</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsCreateOpen(true)}
              className="brand-primary-button flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold transition-all"
            >
              <span>＋</span>
              <span>{copy.newVendor}</span>
            </button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <StatCard label={copy.statVendors} value={String(vendors.length)} detail={copy.statVendorsDetail} isLoading={isLoading} />
          <StatCard label={copy.statCategories} value={String(categoryCount)} detail={copy.statCategoriesDetail} isLoading={isLoading} />
          <StatCard label={copy.statTotalSpend} value={formatAmount(totalSpend, currency)} detail={copy.statTotalSpendDetail} isLoading={isLoading} />
        </div>

        <div className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.allVendors}</h2>
              <p className="mt-1 text-sm text-[#667085]">{copy.shownOf(filteredSummaries.length, vendors.length)}</p>
            </div>

            <label className="relative flex items-center">
              <span className="sr-only">{copy.searchLabel}</span>
              <input
                type="search"
                value={searchQuery}
                onChange={event => setSearchQuery(event.target.value)}
                placeholder={copy.searchPlaceholder}
                className="w-64 rounded-full border border-[#d0d5dd] bg-white px-4 py-2 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
              />
            </label>
          </div>

          {vendors.length === 0 ? (
            <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-6 py-12 text-center">
              <p className="text-sm font-semibold text-[#101828]">{copy.emptyTitle}</p>
              <p className="mt-2 text-sm text-[#667085]">{copy.emptyDetail}</p>
            </div>
          ) : filteredSummaries.length === 0 ? (
            <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-6 py-12 text-center">
              <p className="text-sm font-semibold text-[#101828]">{copy.noMatches(searchQuery)}</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-[24px] border border-[rgba(0,0,0,0.04)]">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-[#fcfcfd] text-[10px] uppercase tracking-[0.22em] text-[#98a2b3]">
                    <th className="px-5 py-3 font-semibold">{copy.colVendor}</th>
                    <th className="px-5 py-3 font-semibold">{copy.colCategory}</th>
                    <th className="px-5 py-3 text-right font-semibold">{copy.colExpenses}</th>
                    <th className="px-5 py-3 text-right font-semibold">{copy.colTotalSpend}</th>
                    <th className="px-5 py-3 font-semibold">{copy.colLastExpense}</th>
                    <th className="px-5 py-3" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filteredSummaries.map(({ vendor, expenseCount, totalSpend: vendorTotal, lastExpenseDate }) => (
                    <tr key={vendor.id} className="border-t border-[#f2f4f7]">
                      <td className="px-5 py-4 font-semibold text-[#101828]">{vendor.name}</td>
                      <td className="px-5 py-4 text-[#667085]">
                        {vendor.category ? (
                          <span className="inline-flex items-center rounded-full bg-[#f2f4f7] px-3 py-1 text-xs font-semibold text-[#475467]">{vendor.category}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-5 py-4 text-right text-[#475467]">{expenseCount}</td>
                      <td className="px-5 py-4 text-right font-bold text-[#101828]">{formatAmount(vendorTotal, currency)}</td>
                      <td className="px-5 py-4 text-[#667085]">{formatDateLabel(lastExpenseDate, language)}</td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => handleStartEdit(vendor)}
                            className="text-xs font-semibold text-[var(--brand-primary)] transition-colors hover:underline"
                          >
                            {copy.edit}
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteVendor(vendor.id)}
                            className="text-xs font-semibold text-[#b42318] transition-colors hover:underline"
                          >
                            {copy.delete}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <AnimatedModal
        open={isCreateOpen}
        onClose={closeCreateModal}
        backdropAriaLabel="Close new vendor dialog"
        overlayClassName="fixed inset-0 z-[80] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-10 w-full max-w-xl rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.createEyebrow}</p>
            <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">{copy.createTitle}</h2>
            <p className="mt-2 text-sm text-[#667085]">{copy.createDescription}</p>
          </div>
          <button
            type="button"
            onClick={closeCreateModal}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleCreate} className="mt-6 space-y-6">
          <div className="grid gap-4">
            <label className="block">
              <span className={modalLabelClassName}>{copy.nameLabel}</span>
              <input
                type="text"
                value={createDraft.name}
                onChange={event => setCreateDraft(current => ({ ...current, name: event.target.value }))}
                placeholder={copy.namePlaceholder}
                required
                className={modalInputClassName}
              />
            </label>

            <label className="block">
              <span className={modalLabelClassName}>{copy.categoryLabel}</span>
              <input
                type="text"
                value={createDraft.category}
                onChange={event => setCreateDraft(current => ({ ...current, category: event.target.value }))}
                placeholder={copy.categoryPlaceholder}
                className={modalInputClassName}
              />
            </label>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={closeCreateModal}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]"
            >
              {copy.cancel}
            </button>
            <button
              type="submit"
              disabled={createDraft.name.trim() === ""}
              className="brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              {copy.addVendor}
            </button>
          </div>
        </form>
      </AnimatedModal>

      <AnimatedModal
        open={editingVendorId !== null}
        onClose={closeEditModal}
        backdropAriaLabel="Close edit vendor dialog"
        overlayClassName="fixed inset-0 z-[80] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-10 w-full max-w-xl rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-400">{copy.editEyebrow}</p>
            <h2 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">{copy.editTitle}</h2>
            <p className="mt-2 text-sm text-[#667085]">{copy.editDescription}</p>
          </div>
          <button
            type="button"
            onClick={closeEditModal}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleEdit} className="mt-6 space-y-6">
          <div className="grid gap-4">
            <label className="block">
              <span className={modalLabelClassName}>{copy.nameLabel}</span>
              <input
                type="text"
                value={editDraft.name}
                onChange={event => setEditDraft(current => ({ ...current, name: event.target.value }))}
                placeholder={copy.namePlaceholder}
                required
                className={modalInputClassName}
              />
            </label>

            <label className="block">
              <span className={modalLabelClassName}>{copy.categoryLabel}</span>
              <input
                type="text"
                value={editDraft.category}
                onChange={event => setEditDraft(current => ({ ...current, category: event.target.value }))}
                placeholder={copy.categoryPlaceholder}
                className={modalInputClassName}
              />
            </label>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={closeEditModal}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]"
            >
              {copy.cancel}
            </button>
            <button
              type="submit"
              disabled={editDraft.name.trim() === ""}
              className="brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all disabled:cursor-not-allowed disabled:opacity-50"
            >
              {copy.saveChanges}
            </button>
          </div>
        </form>
      </AnimatedModal>
    </>
  );
}
